/**
 * Hook pour charger le bootstrap de l'application
 * Appelle /api/me/bootstrap et hydrate le store d'accès
 */

import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAccessStore } from '../useAccessStore'
import { useKeycloak } from '../useKeycloak'
import { httpClient as http } from '@sphere/shared/data-access/http'
import type { Bootstrap } from '@sphere/auth/domain'
import { BOOTSTRAP_CACHE_CONFIG } from '@sphere/shared/util'

/**
 * Génère une clé de query unique par utilisateur et session
 * Cela évite que React Query retourne les données d'un ancien utilisateur
 */
function getBootstrapQueryKey(userId?: string, sessionId?: string) {
  return ['bootstrap', userId || 'unknown', sessionId || 'no-session'] as const
}

/**
 * Récupère le bootstrap depuis l'API
 */
async function fetchBootstrap(): Promise<Bootstrap> {
  try {
    const response = await http.get<Bootstrap>('/me/bootstrap')
    return response.data
  } catch (error: any) {
    // ✅ Si la requête a été annulée car l'utilisateur n'est pas authentifié,
    // on ne propage pas l'erreur pour éviter les logs inutiles
    if (error?.isAuthCanceled) {
      throw new Error('BOOTSTRAP_CANCELED')
    }
    throw error
  }
}

export interface UseBootstrapOptions {
  enabled?: boolean
  staleTime?: number
  retry?: boolean | number
}

/**
 * Hook pour charger et maintenir le bootstrap de l'application
 *
 * @param options - Options de configuration du query
 * @returns Query result avec les données de bootstrap
 *
 * @example
 * ```tsx
 * const { isLoading, error, data } = useBootstrap()
 *
 * if (isLoading) return <Loader />
 * if (error) return <Error />
 *
 * return <App />
 * ```
 */
export function useBootstrap(options: UseBootstrapOptions = {}) {
  const setBootstrap = useAccessStore(state => state.setBootstrap)
  const { tokenParsed } = useKeycloak()

  // 🔑 Utiliser l'ID utilisateur et le sessionId du token pour une queryKey unique
  const userId = tokenParsed?.sub
  const sessionId = tokenParsed?.sid
  const queryKey = getBootstrapQueryKey(userId, sessionId)

  // Fusionner les options avec la config par défaut
  const config = {
    ...BOOTSTRAP_CACHE_CONFIG,
    enabled: options.enabled ?? true,
    staleTime: options.staleTime ?? BOOTSTRAP_CACHE_CONFIG.staleTime,
    retry: options.retry ?? BOOTSTRAP_CACHE_CONFIG.retry,
  }

  const query = useQuery({
    queryKey,
    queryFn: fetchBootstrap,
    ...config,
    // ✅ Ne pas retry si la requête a été annulée (utilisateur non authentifié)
    retry: (failureCount, error: any) => {
      if (error?.message === 'BOOTSTRAP_CANCELED') {
        return false
      }
      return typeof config.retry === 'number'
        ? failureCount < config.retry
        : config.retry
    },
  })

  // Hydrater le store quand les données sont disponibles
  useEffect(() => {
    if (query.data && query.isSuccess) {
      setBootstrap(query.data)
    }
  }, [query.data, query.isSuccess, setBootstrap])

  return query
}

/**
 * Hook pour rafraîchir manuellement le bootstrap
 */
export function useRefreshBootstrap() {
  const queryClient = useQueryClient()
  const setBootstrap = useAccessStore(state => state.setBootstrap)
  const { tokenParsed } = useKeycloak()

  const userId = tokenParsed?.sub
  const sessionId = tokenParsed?.sid

  const refetch = async () => {
    // Invalider le cache pour CET utilisateur spécifiquement
    const queryKey = getBootstrapQueryKey(userId, sessionId)
    await queryClient.invalidateQueries({ queryKey })

    const data = await fetchBootstrap()
    setBootstrap(data)
  }

  return {
    refetch,
    isLoading: false, // Pourrait être amélioré avec un state interne
  }
}
